/**
 * SchoolReportCards - View/print report cards per student per exam
 */
import React, { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Loader2, Printer, FileText, Trophy, GraduationCap, Calendar, BarChart3 } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, { ...opts, headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers } });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

const GRADE_COLORS = {
  'A+': 'text-green-700 bg-green-50', 'A': 'text-green-600 bg-green-50',
  'B+': 'text-blue-700 bg-blue-50', 'B': 'text-blue-600 bg-blue-50',
  'C': 'text-amber-700 bg-amber-50', 'D': 'text-orange-700 bg-orange-50',
  'F': 'text-red-700 bg-red-50',
};

export const SchoolReportCards = ({ tenantId }) => {
  const [selectedExam, setSelectedExam] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const reportRef = useRef();

  const { data: exams = [] } = useQuery({
    queryKey: ['school-exams', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/exams`),
  });

  const { data: classes = [] } = useQuery({
    queryKey: ['school-classes', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/classes`),
  });

  const { data: classSummary, isLoading: loadingSummary } = useQuery({
    queryKey: ['report-cards-class', tenantId, selectedExam, selectedClass],
    queryFn: () => api(`/tenants/${tenantId}/report-cards/${selectedExam}/${selectedClass}`),
    enabled: !!selectedExam && !!selectedClass,
  });

  const { data: reportCard, isLoading: loadingReport } = useQuery({
    queryKey: ['report-card', tenantId, selectedStudent, selectedExam],
    queryFn: () => api(`/tenants/${tenantId}/report-card/${selectedStudent}/${selectedExam}`),
    enabled: !!selectedStudent && !!selectedExam,
  });

  const handlePrint = () => {
    if (!reportRef.current) return;
    const printWindow = window.open('', '', 'width=800,height=1000');
    printWindow.document.write(`
      <html><head><title>Report Card - ${reportCard?.student?.name}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; color: #1e293b; }
        table { width: 100%; border-collapse: collapse; margin: 12px 0; }
        th, td { border: 1px solid #cbd5e1; padding: 6px 10px; text-align: left; font-size: 13px; }
        th { background: #f1f5f9; font-weight: 600; }
        .header { text-align: center; margin-bottom: 16px; border-bottom: 2px solid #334155; padding-bottom: 12px; }
        .header h1 { font-size: 20px; margin: 0; }
        .header p { color: #64748b; font-size: 12px; margin: 2px 0; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 16px; }
        .info-item { font-size: 13px; }
        .info-label { font-weight: 600; color: #475569; }
        .summary { background: #f8fafc; padding: 12px; border-radius: 6px; margin: 12px 0; display: flex; gap: 24px; }
        .summary-item { text-align: center; }
        .summary-value { font-size: 18px; font-weight: 700; }
        .summary-label { font-size: 11px; color: #64748b; }
        .comments { margin-top: 16px; }
        .comments p { font-size: 13px; margin: 4px 0; }
        @media print { body { padding: 0; } }
      </style></head><body>
      ${reportRef.current.innerHTML}
      </body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // INDIVIDUAL REPORT CARD VIEW
  if (selectedStudent && reportCard) {
    const rc = reportCard;
    return (
      <div className="space-y-4" data-testid="report-card-view">
        <div className="flex items-center justify-between">
          <button onClick={() => setSelectedStudent('')} className="text-sm text-indigo-600 hover:underline">
            Back to Class Summary
          </button>
          <Button size="sm" onClick={handlePrint} data-testid="print-report-btn">
            <Printer className="h-4 w-4 mr-1" /> Print
          </Button>
        </div>

        <div ref={reportRef}>
          <Card className="print:shadow-none">
            <CardContent className="p-6">
              {/* School Header */}
              <div className="text-center border-b-2 border-slate-700 pb-4 mb-4">
                <h1 className="text-xl font-bold text-slate-900">{rc.school?.name}</h1>
                <p className="text-sm text-slate-500">{rc.school?.address}</p>
                <p className="text-sm text-slate-500">{rc.school?.phone} | {rc.school?.email}</p>
                <h2 className="text-base font-semibold mt-2">STUDENT REPORT CARD</h2>
                <p className="text-sm text-slate-600">{rc.exam?.name} {rc.academic_year ? `- ${rc.academic_year.name}` : ''}</p>
              </div>

              {/* Student Info */}
              <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                <div><span className="font-semibold text-slate-600">Name: </span>{rc.student?.name}</div>
                <div><span className="font-semibold text-slate-600">Adm No: </span>{rc.student?.admission_number}</div>
                <div><span className="font-semibold text-slate-600">Class: </span>{rc.class?.name || '-'}</div>
                <div><span className="font-semibold text-slate-600">Gender: </span>{rc.student?.gender || '-'}</div>
                {rc.term && <div><span className="font-semibold text-slate-600">Term: </span>{rc.term.name}</div>}
              </div>

              {/* Subject Results */}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Subject</TableHead>
                    <TableHead className="text-center">Marks</TableHead>
                    <TableHead className="text-center">Out Of</TableHead>
                    <TableHead className="text-center">%</TableHead>
                    <TableHead className="text-center">Grade</TableHead>
                    <TableHead>Remarks</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rc.subjects?.map((s, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium">{s.subject_name}</TableCell>
                      <TableCell className="text-center">{s.marks}</TableCell>
                      <TableCell className="text-center">{s.max_marks}</TableCell>
                      <TableCell className="text-center">
                        {s.grade_basis === 'weighted' ? (
                          <span title={(s.weighting || []).map(c => `${c.type} ${c.weight}%${c.percentage === null ? ' (not sat)' : `: ${c.percentage}%`}`).join(' · ')}
                                data-testid={`subject-weighted-${i}`}>
                            <span className="font-semibold">{s.weighted_percentage}%</span>
                            <span className="ml-1 text-[10px] text-slate-400">
                              weighted{s.weighting_provisional ? ' · so far' : ''}
                            </span>
                          </span>
                        ) : `${s.percentage}%`}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${GRADE_COLORS[s.weighted_grade || s.grade] || ''}`}>{s.weighted_grade || s.grade}</span>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">{s.remarks}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Summary */}
              <div className="flex gap-6 bg-slate-50 p-4 rounded-lg mt-4 flex-wrap">
                <div className="text-center">
                  <p className="text-xl font-bold text-slate-900">{rc.summary?.total_marks}/{rc.summary?.total_max}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-indigo-600">{rc.summary?.percentage}%</p>
                  <p className="text-xs text-slate-500">
                    {rc.summary?.grade_basis === 'weighted' ? 'Weighted result' : 'Percentage'}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold">{rc.summary?.grade}</p>
                  <p className="text-xs text-slate-500">Grade</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-amber-600">{rc.summary?.gpa}</p>
                  <p className="text-xs text-slate-500">GPA</p>
                </div>
                {rc.position && (
                  <div className="text-center">
                    <p className="text-xl font-bold text-green-600">{rc.position}/{rc.class_size}</p>
                    <p className="text-xs text-slate-500">Position</p>
                  </div>
                )}
                {rc.pass_fail && (
                  <div className="text-center" data-testid="report-pass-fail">
                    <Badge className={
                      rc.pass_fail.classification === 'distinction' ? 'bg-emerald-100 text-emerald-800' :
                      rc.pass_fail.classification === 'merit' ? 'bg-blue-100 text-blue-800' :
                      rc.pass_fail.classification === 'credit' ? 'bg-indigo-100 text-indigo-800' :
                      rc.pass_fail.is_pass ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'
                    }>
                      {rc.pass_fail.label}
                    </Badge>
                    <p className="text-xs text-slate-500 mt-1">Result · pass ≥ {rc.pass_fail.pass_mark}%</p>
                  </div>
                )}
              </div>

              {rc.summary?.grade_basis === 'weighted' && (
                <p className="mt-2 text-xs text-slate-500" data-testid="report-weighting-note">
                  Graded on the course assessment weightings, not this paper alone
                  {rc.summary?.provisional ? ' — part of the assessment has not been sat yet, so this is the result so far' : ''}.
                  {' '}This paper on its own: {rc.summary?.exam_percentage}%.
                </p>
              )}

              {/* Attendance */}
              {rc.attendance?.total_days > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Attendance Summary</h3>
                  <div className="flex gap-4 text-sm">
                    <span>Days: {rc.attendance.total_days}</span>
                    <span className="text-green-600">Present: {rc.attendance.present}</span>
                    <span className="text-red-600">Absent: {rc.attendance.absent}</span>
                    <span className="text-amber-600">Late: {rc.attendance.late}</span>
                    <span className="font-semibold">Rate: {rc.attendance.rate}%</span>
                  </div>
                </div>
              )}

              {/* Comments */}
              <div className="mt-4 space-y-2 border-t pt-4">
                <div className="text-sm flex gap-4 flex-wrap">
                  <div>
                    <span className="font-semibold text-slate-600">Conduct: </span>
                    <span className="capitalize">{rc.comments?.conduct}</span>
                  </div>
                  {rc.comments?.effort && (
                    <div>
                      <span className="font-semibold text-slate-600">Effort: </span>
                      <span className="capitalize">{rc.comments.effort}</span>
                    </div>
                  )}
                </div>
                {rc.comments?.class_teacher && (
                  <div className="text-sm">
                    <span className="font-semibold text-slate-600">Class Teacher: </span>{rc.comments.class_teacher}
                  </div>
                )}
                {rc.comments?.head_teacher && (
                  <div className="text-sm">
                    <span className="font-semibold text-slate-600">Head Teacher: </span>{rc.comments.head_teacher}
                  </div>
                )}
              </div>

              {/* Grading Key */}
              <div className="mt-4 border-t pt-3">
                <h4 className="text-xs font-semibold text-slate-600 mb-1">Grading Scale</h4>
                <div className="flex flex-wrap gap-2">
                  {rc.grading_scale?.map((g, i) => (
                    <span key={i} className="text-[10px] bg-slate-100 px-2 py-0.5 rounded">
                      {g.grade}: {g.min}-{g.max}%
                    </span>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // CLASS SUMMARY VIEW / SELECTOR
  return (
    <div className="space-y-6" data-testid="report-cards-panel">
      <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
        <FileText className="h-5 w-5" /> Report Cards
      </h2>

      <div className="flex flex-wrap gap-3">
        <div className="min-w-0 sm:min-w-[200px]">
          <label className="text-sm font-medium text-slate-700 mb-1 block">Exam</label>
          <Select value={selectedExam} onValueChange={v => { setSelectedExam(v); setSelectedStudent(''); }}>
            <SelectTrigger data-testid="report-exam-select"><SelectValue placeholder="Select exam" /></SelectTrigger>
            <SelectContent>
              {exams.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-0 sm:min-w-[200px]">
          <label className="text-sm font-medium text-slate-700 mb-1 block">Class</label>
          <Select value={selectedClass} onValueChange={v => { setSelectedClass(v); setSelectedStudent(''); }}>
            <SelectTrigger data-testid="report-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
            <SelectContent>
              {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!selectedExam || !selectedClass ? (
        <Card className="border-dashed border-2">
          <CardContent className="py-12 text-center text-slate-400">
            <FileText className="h-10 w-10 mx-auto mb-3 opacity-50" />
            Select an exam and class to view report cards
          </CardContent>
        </Card>
      ) : loadingSummary ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {classSummary?.class_name} - {classSummary?.exam_name}
            </CardTitle>
          </CardHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">Pos</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Adm No.</TableHead>
                <TableHead className="text-center">Total</TableHead>
                <TableHead className="text-center">%</TableHead>
                <TableHead className="text-center">Grade</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!classSummary?.cards?.length ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No marks entered yet</TableCell></TableRow>
              ) : classSummary.cards.map(card => (
                <TableRow key={card.student_id}>
                  <TableCell>
                    {card.position <= 3 ? (
                      <Badge className={card.position === 1 ? 'bg-yellow-100 text-yellow-800' : card.position === 2 ? 'bg-slate-200 text-slate-700' : 'bg-amber-100 text-amber-700'}>
                        #{card.position}
                      </Badge>
                    ) : <span className="text-slate-500">#{card.position}</span>}
                  </TableCell>
                  <TableCell className="font-medium">{card.student_name}</TableCell>
                  <TableCell className="text-sm text-slate-500">{card.admission_number}</TableCell>
                  <TableCell className="text-center">{card.total_marks}/{card.total_max}</TableCell>
                  <TableCell className="text-center font-semibold">{card.percentage}%</TableCell>
                  <TableCell className="text-center">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${GRADE_COLORS[card.grade] || ''}`}>{card.grade}</span>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedStudent(card.student_id)}
                      data-testid={`view-report-${card.student_id}`}
                    >
                      View Report
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};

export default SchoolReportCards;
